/**
 * Product Inquiry System — modal logic & AJAX submission
 * -----------------------------------------------------
 * Vanilla JS, no dependencies. One controller per <inquiry-widget> on the page.
 * Submits via the EmailJS REST API using template_whrxbb5.
 *
 * Behaviour: slide-over open/close, backdrop + Esc, focus trap, quick-question
 * chips, char counter, client-side validation, loading/success/error states,
 * localStorage drafts, and a client-side reference number for the customer.
 */
(function () {
  'use strict';

  // Per-widget guard lives on root.__iq (set in setupWidget).
  // MutationObserver at the bottom handles widgets injected after DOMContentLoaded.

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function ref() {
    var d = new Date();
    var stamp = String(d.getFullYear()).slice(2) + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    return 'IQ-' + stamp + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
  }

  function setupWidget(root) {
    if (root.__iq) return;

    var panel     = root.querySelector('.iq__panel');
    var backdrop  = root.querySelector('[data-iq-backdrop]');
    var form      = root.querySelector('[data-iq-form]');
    var success   = root.querySelector('[data-iq-success]');
    var submitBtn = root.querySelector('[data-iq-submit]');
    if (!panel || !backdrop || !form || !success || !submitBtn) return;
    root.__iq = true;

    var textarea  = form.querySelector('textarea[name="contact[body]"]');
    var counter   = root.querySelector('[data-iq-counter]');
    var draftKey  = 'iq-draft-' + root.dataset.productId;
    var lastFocus = null;

    // Stamp the currently selected variant id (best effort).
    var variantField = form.querySelector('[data-iq-variant]');
    function syncVariant() {
      var v = new URLSearchParams(location.search).get('variant');
      if (variantField) variantField.value = v || '';
    }

    /* ── Open / close ── */
    function open() {
      lastFocus = document.activeElement;
      syncVariant();
      // Lift to <body> while open so no section's stacking context can overlap the drawer.
      if (backdrop.parentNode !== document.body) document.body.appendChild(backdrop);
      if (panel.parentNode !== document.body) document.body.appendChild(panel);
      backdrop.hidden = false;
      panel.hidden = false;
      requestAnimationFrame(function () {
        backdrop.setAttribute('data-open', '');
        panel.setAttribute('data-open', '');
      });
      document.body.style.overflow = 'hidden';
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener('keydown', onKey);
    }

    function close() {
      backdrop.removeAttribute('data-open');
      panel.removeAttribute('data-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      setTimeout(function () {
        backdrop.hidden = true;
        panel.hidden = true;
        // Return to original DOM position so the at-rest markup stays unchanged.
        root.appendChild(backdrop);
        root.appendChild(panel);
      }, 340);
      if (lastFocus) lastFocus.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    root.querySelectorAll('[data-iq-open]').forEach(function (b) { b.addEventListener('click', open); });
    root.querySelectorAll('[data-iq-close]').forEach(function (b) { b.addEventListener('click', close); });
    backdrop.addEventListener('click', close);

    /* ── Quick question chips ── */
    root.querySelectorAll('[data-iq-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var q = chip.getAttribute('data-iq-chip');
        var cur = textarea.value.trim();
        textarea.value = cur ? cur.replace(/\s*$/, '') + '\n' + q : q;
        chip.setAttribute('aria-pressed', 'true');
        updateCounter();
        textarea.focus();
      });
    });

    /* ── Quick-topic rotation: show 5 chips, cycle groups every 5s ── */
    (function setupChipRotation() {
      var track = root.querySelector('[data-iq-chip-track]');
      if (!track) return;
      var chips = Array.prototype.slice.call(track.querySelectorAll('[data-iq-chip]'));
      var PER = 5;
      if (chips.length <= PER) return; // nothing to rotate

      var groups = [];
      for (var i = 0; i < chips.length; i += PER) groups.push(chips.slice(i, i + PER));

      var current = 0;
      function show(idx) {
        groups.forEach(function (g, gi) {
          g.forEach(function (chip) { chip.style.display = gi === idx ? '' : 'none'; });
        });
      }
      show(0);

      var timer = null;
      function start() {
        stop();
        timer = setInterval(function () {
          track.style.opacity = '0';
          setTimeout(function () {
            current = (current + 1) % groups.length;
            show(current);
            track.style.opacity = '1';
          }, 220);
        }, 5000);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      // Pause while the visitor is reading or interacting with the chips.
      track.addEventListener('mouseenter', stop);
      track.addEventListener('mouseleave', start);
      track.addEventListener('focusin', stop);
      track.addEventListener('focusout', start);

      start();
    })();

    /* ── Char counter + draft persistence ── */
    function updateCounter() {
      counter.textContent = textarea.value.length + ' / 500';
    }
    try {
      var saved = localStorage.getItem(draftKey);
      if (saved) { textarea.value = saved; }
    } catch (e) {}
    textarea.addEventListener('input', function () {
      updateCounter();
      try { localStorage.setItem(draftKey, textarea.value); } catch (e) {}
    });
    updateCounter();

    /* ── Validation ── */
    function showError(field, msg) {
      var wrap = field.closest('.iq__field');
      var err  = wrap.querySelector('[data-iq-err]');
      wrap.setAttribute('data-invalid', '');
      if (err) { err.textContent = msg; err.hidden = false; }
      field.setAttribute('aria-invalid', 'true');
    }
    function clearError(field) {
      var wrap = field.closest('.iq__field');
      var err  = wrap.querySelector('[data-iq-err]');
      wrap.removeAttribute('data-invalid');
      if (err) { err.hidden = true; }
      field.removeAttribute('aria-invalid');
    }
    form.querySelectorAll('input,textarea').forEach(function (f) {
      f.addEventListener('input', function () { clearError(f); });
    });

    function validate() {
      var ok    = true;
      var name  = form.querySelector('[name="contact[name]"]');
      var email = form.querySelector('[name="contact[email]"]');
      if (!name.value.trim()) { showError(name, 'Please enter your name.'); ok = false; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.value.trim())) { showError(email, 'Please enter a valid email.'); ok = false; }
      if (textarea.value.trim().length < 10) { showError(textarea, 'Please enter at least 10 characters.'); ok = false; }
      return ok;
    }

    /* ── Submit — EmailJS ── */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!validate()) {
        var bad = form.querySelector('[data-invalid] input, [data-invalid] textarea');
        if (bad) bad.focus();
        return;
      }

      var name     = form.querySelector('[name="contact[name]"]').value.trim();
      var email    = form.querySelector('[name="contact[email]"]').value.trim();
      var question = textarea.value.trim();
      var variant  = variantField && variantField.value ? variantField.value : '—';
      var title    = root.dataset.productTitle;

      if (variantField) variantField.value = variant;

      submitBtn.setAttribute('data-loading', '');
      submitBtn.disabled = true;

      fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id:  root.dataset.emailjsService,
          template_id: root.dataset.emailjsTemplate,
          user_id:     root.dataset.emailjsKey,
          template_params: {
            // Header fields / variables:
            title:            title,
            name:             name,
            email:            email,
            // Body variables (using multiple aliases for maximum template compatibility):
            from_name:        name,
            from_email:       email,
            reply_to:         email,
            message:          question,
            customer_message: question,
            product_title:    title,
            product_price:    root.dataset.productPrice,
            product_url:      root.dataset.productUrl,
            product_id:       root.dataset.productId,
            variant_id:       variant
          }
        })
      }).then(function (res) {
        if (res.ok) return res.text();
        throw new Error('HTTP ' + res.status);
      }).then(function () {
        try { localStorage.removeItem(draftKey); } catch (e) {}
        var refEl = root.querySelector('[data-iq-ref]');
        if (refEl) refEl.textContent = ref();
        form.hidden = true;
        success.hidden = false;
        var scrollEl = root.querySelector('[data-iq-scroll]');
        if (scrollEl) scrollEl.scrollTop = 0;
      }).catch(function (err) {
        console.error('[inquiry-modal] submit error:', err);
        showError(textarea, 'Something went wrong. Please try again or email us directly.');
      }).finally(function () {
        submitBtn.removeAttribute('data-loading');
        submitBtn.disabled = false;
      });
    });

    /* ── Reset to send another ── */
    var resetBtn = root.querySelector('[data-iq-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        form.reset();
        success.hidden = true;
        form.hidden = false;
        root.querySelectorAll('[data-iq-chip]').forEach(function (c) { c.removeAttribute('aria-pressed'); });
        updateCounter();
      });
    }
  }

  function init() {
    document.querySelectorAll('inquiry-widget').forEach(setupWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Auto-init widgets injected after DOMContentLoaded (Shopify section AJAX,
  // theme editor live-preview, quick-view drawers, etc.)
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'INQUIRY-WIDGET') { setupWidget(node); return; }
          node.querySelectorAll && node.querySelectorAll('inquiry-widget').forEach(setupWidget);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
